import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';

import { delay, Observable, of } from 'rxjs';
import { AdminFinancialRequest, AdminFinancialRequestAssigneeCategory } from '@pt/models';

import {
  ChangeRequestStatusReq,
  ChangeRequestStatusRes,
  CreateRequestReq,
  CreateRequestRes,
  GetFinancialCategoriesRes,
  GetLocationsRes,
  GetRequestDetailsEncryptedDataReq,
  GetRequestDetailsEncryptedDataRes,
  GetRequestDetailsReq,
  GetRequestDetailsRes,
  GetRequestIdHashRes,
  GetRequestsReq,
  GetRequestsRes,
  GetUsersByIdsReq,
  GetUsersByIdsRes,
  SaveRequestEncryptedData,
} from '../models';
import { FinancialRequestsApi } from './financial-requests-api.interface';
import {
  getFinancialCategoriesMock,
  getRequestDetailsEncryptedDataMock,
  getRequestDetailsMock,
  getRequestsMock,
  getUsersByIds,
} from '../mocks';
import { GetAssigneesRes } from '../models/get-assignees-res.model';
import { getAssigneesMock } from '../mocks/get-assignees.mock';
import { GetServicesRes } from '../models/get-services-res.model';
import { GetFunctionsRes } from '../models/get-functions-res.model';
import { GetTeamsRes } from '../models/get-teams-res.model';
import { GetBusinessUnitsRes } from '../models/get-business-units-res.model';

@Injectable()
export class FinancialRequestsMockService implements FinancialRequestsApi {
  getRequestIdHash(): Observable<GetRequestIdHashRes> {
    return of({
      request: {
        requestIdHash: '937251f04cb8e76acccdb19d8bb27a20e59ee349d2f374fd705f1683c4e62f96',
      },
    }).pipe(delay(500));
  }

  createRequest(params: CreateRequestReq): Observable<CreateRequestRes> {
    return of(getRequestDetailsMock(3)).pipe(delay(500));
  }

  patchRequestAssigneeCategory(
    id: number,
    params: AdminFinancialRequestAssigneeCategory,
  ): Observable<AdminFinancialRequest> {
    return of(null);
  }

  saveRequestEncryptedData(
    params: SaveRequestEncryptedData,
  ): Observable<SaveRequestEncryptedData> {
    return of(
      getRequestDetailsEncryptedDataMock(params.requestIdHash).financialMessages[0],
    ).pipe(delay(500));
  }

  getRequests(params: GetRequestsReq): Observable<GetRequestsRes> {
    const { requests, itemsCount } = getRequestsMock();
    return of({ requests, itemsCount }).pipe(delay(500));
  }

  downloadReport(params: GetRequestsReq, isAssignedToMe: boolean): Observable<HttpResponse<Blob>> {
    return of(null).pipe(delay(500));
  }

  getLocations(): Observable<GetLocationsRes> {
    return of({ locations: ['UA IT', 'CY HQ', 'KZ CS COM', 'UA CS Kyiv'] }).pipe(delay(500));
  }

  getServices(): Observable<GetServicesRes> {
    return of({ services: ['Serv1', 'Serv2'] }).pipe(delay(500));
  }

  getBusinessUnits(): Observable<GetBusinessUnitsRes> {
    return of({ businessUnits: ['BU1', 'BU2'] }).pipe(delay(500));
  }

  getFunctions(): Observable<GetFunctionsRes> {
    return of({ functions: ['Func1', 'Func2'] }).pipe(delay(500));
  }

  getTeams(): Observable<GetTeamsRes> {
    return of({ teams: ['Team1', 'Team2'] }).pipe(delay(500));
  }

  getFinancialCategories(): Observable<GetFinancialCategoriesRes> {
    return of(getFinancialCategoriesMock()).pipe(delay(500));
  }

  getAssignees(): Observable<GetAssigneesRes> {
    return of(getAssigneesMock()).pipe(delay(500));
  }

  getRequestDetails(params: GetRequestDetailsReq): Observable<GetRequestDetailsRes> {
    return of(getRequestDetailsMock(params.requestId)).pipe(delay(500));
  }

  getRequestDetailsEncryptedData(
    params: GetRequestDetailsEncryptedDataReq,
  ): Observable<GetRequestDetailsEncryptedDataRes> {
    return of(getRequestDetailsEncryptedDataMock(params.requestIdHash)).pipe(delay(500));
  }

  getUsersByIds(params: GetUsersByIdsReq): Observable<GetUsersByIdsRes> {
    return of(getUsersByIds()).pipe(delay(500));
  }

  changeRequestStatus(params: ChangeRequestStatusReq): Observable<ChangeRequestStatusRes> {
    return of(getRequestDetailsMock(params.requestId) as ChangeRequestStatusRes).pipe(delay(500));
  }
}
